import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Chip, Input, Select } from "@legal-500/legal500-design-system";
import type { Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { useQuery } from "@/hooks/useApi";
import type { TaxonomyMode } from "@/api/submissions.service";
import {
  surveysService,
  type SurveyResponse,
  type SurveyResponseStatus,
} from "@/api/surveys.service";
import { newAxisEditionOptions } from "@/api/surveyTaxonomy.service";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { usePublications } from "@/hooks/usePublications";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { taxonomyFilterParams } from "@/utils/taxonomyFilter";
import { ROUTES } from "@/routes";
import { formatDateTime } from "@/utils";
import styles from "./Responses.module.css";

const DEBOUNCE_MS = 300;

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "completed", label: "Completed" },
];

const STATUS_CHIP_COLOR: Record<SurveyResponseStatus, "success" | "default"> = {
  completed: "success",
  draft: "default",
};

const PAGE_SIZE = 50;

function SurveyResponsesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const { toggleVisible, mode, taxonomy, ready } = useTaxonomyMode();

  const [firmRef, setFirmRef] = useState("");
  const [refereeEmail, setRefereeEmail] = useState("");
  const [edition, setEdition] = useState("");
  const [publication, setPublication] = useState("");
  const [status, setStatus] = useState("");

  const [debouncedFirmRef, setDebouncedFirmRef] = useState("");
  const [debouncedRefereeEmail, setDebouncedRefereeEmail] = useState("");

  const firmRefTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refereeEmailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentPage = Number(searchParams.get("page") ?? "1") || 1;

  useEffect(() => {
    if (firmRefTimerRef.current) clearTimeout(firmRefTimerRef.current);
    firmRefTimerRef.current = setTimeout(() => {
      setDebouncedFirmRef(firmRef);
    }, DEBOUNCE_MS);
    return () => {
      if (firmRefTimerRef.current) clearTimeout(firmRefTimerRef.current);
    };
  }, [firmRef]);

  useEffect(() => {
    if (refereeEmailTimerRef.current) clearTimeout(refereeEmailTimerRef.current);
    refereeEmailTimerRef.current = setTimeout(() => {
      setDebouncedRefereeEmail(refereeEmail);
    }, DEBOUNCE_MS);
    return () => {
      if (refereeEmailTimerRef.current) clearTimeout(refereeEmailTimerRef.current);
    };
  }, [refereeEmail]);

  useEffect(() => {
    if (!ready) return;
    setPublication("");
  }, [ready, mode]);

  useEffect(() => {
    setSearchParams(
      prev => {
        prev.set("page", "1");
        return prev;
      },
      { replace: true },
    );
  }, [debouncedFirmRef, debouncedRefereeEmail, edition, publication, status]);

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [{ value: "", label: "All" }];
    for (let year = currentYear + 1; year >= 2017; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const { data: publicationsData } = usePublications(
    { edition: edition || undefined },
    { enabled: ready && mode === "legacy" && !!edition },
  );

  const { data: newAxisEditions } = useNewAxisEditions({
    enabled: ready && mode === "new" && !!edition,
  });

  const publicationOptions = useMemo(() => {
    if (mode === "new") {
      return [
        { value: "", label: "Select Publication" },
        ...newAxisEditionOptions(newAxisEditions, edition),
      ];
    }
    const pubs = publicationsData?.publications ?? [];
    return [
      { value: "", label: "Select Publication" },
      ...pubs.map(p => ({
        value: String(p.id),
        label: p.publication ? `${p.publication} (${p.edition ?? "n/a"})` : `Publication ${p.id}`,
      })),
    ];
  }, [mode, newAxisEditions, edition, publicationsData?.publications]);

  const { publicationId, editionId } = taxonomyFilterParams("publication", publication, mode);

  const offset = (currentPage - 1) * PAGE_SIZE;

  const { data, loading, error, isRefetching, refetch } = useQuery(
    () =>
      ready
        ? surveysService.listResponses({
            firmRef: debouncedFirmRef || undefined,
            refereeEmail: debouncedRefereeEmail || undefined,
            edition: publicationId === undefined ? edition || undefined : undefined,
            publicationId,
            editionId,
            taxonomy,
            status: status || undefined,
            limit: PAGE_SIZE,
            offset,
          })
        : Promise.resolve(null),
    [
      ready,
      debouncedFirmRef,
      debouncedRefereeEmail,
      edition,
      publicationId,
      editionId,
      taxonomy,
      status,
      currentPage,
    ],
  );

  const responses = data?.responses ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    setSearchParams(
      prev => {
        prev.set(TAXONOMY_URL_PARAM, next);
        prev.set("page", "1");
        return prev;
      },
      { replace: true },
    );
  };

  const columns: Column<SurveyResponse>[] = useMemo(
    () => [
      {
        header: "Firm Ref",
        accessor: "firmRef" as keyof SurveyResponse,
      },
      {
        header: "Referee Email",
        accessor: "refereeEmail" as keyof SurveyResponse,
      },
      {
        header: "Edition",
        accessor: "edition" as keyof SurveyResponse,
      },

      {
        header: "Status",
        accessor: "status" as keyof SurveyResponse,
        render: (value: unknown) => {
          const status = value as SurveyResponseStatus;
          return (
            <Chip
              size="small"
              color={STATUS_CHIP_COLOR[status]}
              className={status === "draft" ? styles.draftStatusChip : undefined}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Chip>
          );
        },
      },
      {
        header: "Locale",
        accessor: "locale" as keyof SurveyResponse,
        render: (value: unknown) => <span>{String(value).toUpperCase()}</span>,
      },
      {
        header: "Completed At",
        accessor: "completedAt" as keyof SurveyResponse,
        render: (value: unknown) => (value ? formatDateTime(value as string) : "—"),
      },
      {
        header: "Actions",
        accessor: "id" as keyof SurveyResponse,
        render: (_value: unknown, row: SurveyResponse) => (
          <Button
            variant="outlined"
            size="small"
            onClick={event => {
              event.stopPropagation();
              navigate(ROUTES.surveys.responseDetail(row.id));
            }}
          >
            View
          </Button>
        ),
      },
    ],
    [navigate],
  );

  if (!ready || (loading && !isRefetching)) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Survey Responses" />
        <LoadingState message="Loading responses..." />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Survey Responses" subtitle="Browse referee survey submissions" />

      {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}

      <div className={styles.filterRow}>
        <div className={styles.filters}>
          <div className={styles.filterField}>
            <Input label="Firm Ref" value={firmRef} onChange={e => setFirmRef(e.target.value)} />
          </div>
          <div className={styles.filterField}>
            <Input
              label="Referee Email"
              value={refereeEmail}
              onChange={e => setRefereeEmail(e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Edition"
              value={edition}
              onChange={e => {
                setEdition(e.target.value);
                setPublication("");
              }}
              options={editionOptions}
              size="small"
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Publication"
              value={publication}
              onChange={e => setPublication(e.target.value)}
              options={publicationOptions}
              size="small"
              disabled={!edition}
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Status"
              value={status}
              onChange={e => setStatus(e.target.value)}
              options={STATUS_OPTIONS}
              size="small"
            />
          </div>
        </div>
      </div>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {!error && responses.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No responses found"
          message="No responses match your filter criteria. Try adjusting your filters."
        />
      ) : (
        !error && (
          <>
            <div className={styles.tableContainer}>
              <div className={styles.scopedTable}>
                <DataTable
                  data={responses}
                  columns={columns}
                  onRowClick={row => navigate(ROUTES.surveys.responseDetail(row.id))}
                  emptyMessage="No responses found matching your criteria."
                />
              </div>
            </div>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + responses.length}
              totalCount={totalCount}
              itemName="responses"
              onPageChange={newPage =>
                setSearchParams(
                  prev => {
                    prev.set("page", String(newPage));
                    return prev;
                  },
                  { replace: true },
                )
              }
            />
          </>
        )
      )}
    </div>
  );
}

export default SurveyResponsesPage;
